package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.ROSTER_PAGE_SIZE
import br.gov.go.pge.permanenciaweb.site.pages.loadSemSaida
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

val SemSaidaViewStyle = CssStyle.base {
    Modifier.padding(top = 0.cssRem, right = 1.5.cssRem, bottom = 1.5.cssRem, left = 1.5.cssRem)
}

@Composable
internal fun SemSaidaPanel(state: DashboardState) {
    LaunchedEffect(Unit) {
        state.loadSemSaida()
    }

    val allRows = state.semSaidaRows
    val rows = allRows
        .applyColumnFilters(
            filters = state.filters.forTable(SEM_SAIDA_FILTER_KEYS),
            text = { row, key -> key.semSaidaText(row) },
        )
        .sortedWithCriteria(state.semSaidaSort) { row, field -> field.text(row) }
    val facets = remember(allRows) { semSaidaFacetOptions(allRows) }

    val pages = pageCountOf(rows.size, ROSTER_PAGE_SIZE)
    val currentPage = state.semSaidaPage.coerceIn(1, pages.coerceAtLeast(1))
    val visibleRows = rows.pageSlice(currentPage, ROSTER_PAGE_SIZE)

    Section(attrs = SemSaidaViewStyle.toModifier().then(Modifier.id("sem-saida-view")).toAttrs()) {
        Panel {
            PanelHeader(
                title = {
                    Text("Sem saída (30 dias) ")
                    when {
                        allRows.isEmpty() -> Unit
                        rows.size != allRows.size -> Text("— ${rows.size} de ${allRows.size}")
                        else -> Text("— ${rows.size}")
                    }
                },
                actions = { ClearFiltersButton(state, SEM_SAIDA_SCOPE) },
            )
            TableScroll {
                Table(attrs = TableStyle.toAttrs()) {
                    Thead {
                        Tr {
                            SemSaidaHeaderCell(
                                "Nome",
                                SemSaidaSortField.NOME,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Cad. Portaria",
                                SemSaidaSortField.TIPO,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Cargo",
                                SemSaidaSortField.CARGO,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Setor",
                                SemSaidaSortField.SETOR,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Sala",
                                SemSaidaSortField.SALA,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Andar",
                                SemSaidaSortField.ANDAR,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Unidade",
                                SemSaidaSortField.UNIDADE,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Sem saída",
                                SemSaidaSortField.DIAS,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                numeric = true,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                            SemSaidaHeaderCell(
                                "Último dia",
                                SemSaidaSortField.ULTIMO_DIA,
                                state.semSaidaSort,
                                state.filters,
                                facets,
                                onSort = state::toggleSemSaidaSort,
                                onFilterChange = state::updateFilter,
                            )
                        }
                    }
                    Tbody {
                        for (row in visibleRows) {
                            Tr(attrs = TableRowHoverStyle.toAttrs()) {
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.nome) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.tipoAcesso.orEmpty()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.cargo.orEmpty()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.setor.orEmpty()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.sala.orEmpty()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.andar.orEmpty()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.unidade.orEmpty()) }
                                Td(attrs = TableNumericCellStyle.toAttrs()) { Text(row.diasSemSaida.toString()) }
                                Td(attrs = TableCellStyle.toAttrs()) { Text(row.ultimoDia) }
                            }
                        }
                    }
                }
            }
            if (rows.isEmpty()) {
                P(attrs = PanelEmptyStyle.toAttrs()) {
                    Text(
                        when {
                            state.semSaidaLoading -> "Carregando…"
                            !state.semSaidaLoaded -> state.semSaidaLoadError ?: "Carregando…"
                            state.hasFilters(SEM_SAIDA_SCOPE) -> "Nenhum fechamento para os filtros atuais."
                            else -> "Nenhum fechamento sem liberação nos últimos 30 dias."
                        }
                    )
                }
            }
            Pagination(page = currentPage, pageCount = pages, onPage = { state.semSaidaPage = it })
        }
    }
}

@Composable
private fun SemSaidaHeaderCell(
    label: String,
    field: SemSaidaSortField,
    sort: List<Criterion<SemSaidaSortField>>,
    filters: Map<FilterKey, ColumnFilter>,
    facets: Map<FilterKey, List<String>>,
    numeric: Boolean = false,
    onSort: (SemSaidaSortField, Boolean) -> Unit,
    onFilterChange: (FilterKey, ColumnFilter) -> Unit,
) = SortableTh(
    label = label,
    field = field,
    criteria = sort,
    filter = filters[field.filterKey] ?: ColumnFilter.Empty,
    filterId = "sem-saida-${field.name.lowercase()}",
    options = facets[field.filterKey],
    numeric = numeric,
    onSort = onSort,
    onFilterChange = { onFilterChange(field.filterKey, it) },
)
